import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Article } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import './BlogEditor.css';

interface BlogEditorProps {
  articleId?: number;
  onSave: () => void;
  onCancel: () => void;
}

const BlogEditor: React.FC<BlogEditorProps> = ({ articleId, onSave, onCancel }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (articleId) {
      fetchArticle();
    }
  }, [articleId]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/blog/articles/${articleId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data: Article = await response.json();
        setTitle(data.title);
        setContent(data.content);
        setExcerpt(data.excerpt || '');
        setFeaturedImage(data.featured_image || '');
        setStatus(data.status);
      } else {
        setError('Failed to load article');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (submitStatus: 'draft' | 'published') => {
    if (!title.trim()) {
      setError(t('blog.titleRequired') || 'Title is required');
      return;
    }
    if (!content.trim()) {
      setError(t('blog.contentRequired') || 'Content is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        title: title.trim(),
        content: content.trim(),
        excerpt: excerpt.trim(),
        featured_image: featuredImage.trim() || null,
        status: submitStatus
      };

      const url = articleId
        ? `${API_BASE_URL}/api/blog/articles/${articleId}`
        : `${API_BASE_URL}/api/blog/articles`;

      const response = await fetch(url, {
        method: articleId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSave();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save article');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="blog-editor-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="blog-editor">
      <div className="blog-editor-header">
        <h2>{articleId ? t('blog.editArticle') : t('blog.createArticle')}</h2>
        <div className="blog-editor-actions">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="btn btn-preview"
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button onClick={onCancel} className="btn btn-cancel">
            {t('common.cancel')}
          </button>
        </div>
      </div>

      {error && <div className="blog-editor-error">{error}</div>}

      {showPreview ? (
        <div className="blog-editor-preview">
          <h1>{title || 'Untitled'}</h1>
          {featuredImage && (
            <img src={featuredImage} alt={title} className="preview-image" />
          )}
          <div className="preview-content">
            <ReactMarkdown>{content || '*No content*'}</ReactMarkdown>
          </div>
        </div>
      ) : (
        <form className="blog-editor-form">
          <div className="form-group">
            <label htmlFor="title">{t('blog.title')}</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter article title..."
              maxLength={255}
            />
          </div>

          <div className="form-group">
            <label htmlFor="excerpt">{t('blog.excerpt')}</label>
            <textarea
              id="excerpt"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Short description of the article..."
              rows={3}
              maxLength={500}
            />
            <span className="char-count">{excerpt.length}/500</span>
          </div>

          <div className="form-group">
            <label htmlFor="featuredImage">Featured Image URL</label>
            <input
              type="url"
              id="featuredImage"
              value={featuredImage}
              onChange={(e) => setFeaturedImage(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">{t('blog.content')}</label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your article in Markdown..."
              rows={20}
            />
            <span className="markdown-hint">
              Supports Markdown: **bold**, *italic*, # headers, - lists, [links](url), ![images](url)
            </span>
          </div>
        </form>
      )}

      <div className="blog-editor-footer">
        <button
          onClick={() => handleSubmit('draft')}
          className="btn btn-draft"
          disabled={saving}
        >
          {saving ? 'Saving...' : t('blog.saveDraft')}
        </button>
        <button
          onClick={() => handleSubmit('published')}
          className="btn btn-publish"
          disabled={saving}
        >
          {saving ? 'Publishing...' : t('blog.publish')}
        </button>
      </div>
    </div>
  );
};

export default BlogEditor;
